import React, {useEffect, useState} from "react";
import {createPortal} from "react-dom";
import {useMutation} from "@tanstack/react-query";
import {request} from "../../lib/request";
import {getErrorMessage, toastError, toastSuccess} from "../../lib/toast";

const ModalReplay = ({id, refetch, text}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [replyText, setReplyText] = useState(text || "");

    useEffect(() => {
        if (!isOpen) {
            return undefined;
        }

        const onKeyDown = (event) => {
            if (event.key === "Escape") {
                setIsOpen(false);
            }
        };

        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        window.addEventListener("keydown", onKeyDown);

        return () => {
            document.body.style.overflow = previousOverflow;
            window.removeEventListener("keydown", onKeyDown);
        };
    }, [isOpen]);

    useEffect(() => {
        if (isOpen) {
            setReplyText(text || "");
        }
    }, [isOpen, text]);

    const {mutate, isPending} = useMutation({
        mutationFn: async (data) =>
            await request({
                url: `/restaurant/comments/reply/${id}`,
                method: "post",
                data,
            }),
        onSuccess: (data) => {
            toastSuccess(data.message || "پاسخ با موفقیت ثبت شد");
            setIsOpen(false);
            refetch();
        },
        onError: (err) => {
            toastError(getErrorMessage(err));
        },
    });

    const openModal = () => setIsOpen(true);

    const closeModal = () => {
        if (!isPending) {
            setIsOpen(false);
        }
    };

    const submitReply = () => {
        mutate({text: replyText.trim()});
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        submitReply();
    };

    return (
        <>
            <button
                type="button"
                onClick={openModal}
                className="rest-action-btn rest-action-btn--primary rest-action-btn--compact shrink-0"
            >
                پاسخ به دیدگاه
            </button>

            {isOpen && createPortal(
                <div
                    className="rest-accept-modal"
                    role="dialog"
                    aria-modal="true"
                    aria-label="پاسخ به دیدگاه کاربر"
                    dir="rtl"
                >
                    <button
                        type="button"
                        className="rest-accept-modal__backdrop"
                        onClick={closeModal}
                        aria-label="بستن"
                        disabled={isPending}
                    />

                    <div className="rest-accept-modal__sheet">
                        <div className="rest-accept-modal__accent" aria-hidden="true"/>

                        <form onSubmit={handleSubmit} className="rest-accept-modal__form">
                            <h2 className="rest-accept-modal__title">پاسخ به دیدگاه کاربر</h2>

                            <div className="rest-accept-modal__body">
                                <div className="rest-accept-modal__field">
                                    <label
                                        className="rest-accept-modal__label"
                                        htmlFor={`comment-reply-${id}`}
                                    >
                                        متن پاسخ
                                    </label>
                                    <textarea
                                        id={`comment-reply-${id}`}
                                        className="rest-accept-modal__textarea"
                                        value={replyText}
                                        onChange={(event) => setReplyText(event.target.value)}
                                        disabled={isPending}
                                        rows={4}
                                        placeholder="پاسخ خود را بنویسید..."
                                    />
                                </div>
                            </div>

                            <div className="rest-accept-modal__actions">
                                <button
                                    type="submit"
                                    disabled={isPending}
                                    className="rest-action-btn rest-action-btn--primary"
                                >
                                    {isPending ? "در حال ثبت..." : "ثبت پاسخ"}
                                </button>
                                <button
                                    type="button"
                                    disabled={isPending}
                                    onClick={closeModal}
                                    className="rest-action-btn rest-action-btn--ghost"
                                >
                                    انصراف
                                </button>
                            </div>
                        </form>
                    </div>
                </div>,
                document.body
            )}
        </>
    );
};

export default ModalReplay;
